import {
	TodayFocusEvent,
	TodayFocusItem,
	TodayFocusQueues,
} from "../../../store/ducks/operationalDashboard";

export interface TodayFocusResponse {
	limitePorTipo: number;
	focoHoje: TodayFocusQueues;
	geradoEm: string | null;
}

export class TodayFocusApiError extends Error {
	status: number;

	constructor(status: number, message: string) {
		super(message);
		this.name = "TodayFocusApiError";
		this.status = status;
	}
}

const parseResponse = async (response: Response) => {
	try {
		return await response.json();
	} catch (_) {
		throw new TodayFocusApiError(
			response.status,
			"Não foi possível interpretar a resposta do serviço."
		);
	}
};

const isQueue = (value: any) =>
	value && Array.isArray(value.items) && typeof value.temProxima === "boolean";

const isQueues = (value: any): value is TodayFocusQueues =>
	isQueue(value?.compartilharRelatorio) &&
	isQueue(value?.convidarReavaliacao) &&
	isQueue(value?.resgatarCliente);

const todayFocusRequests = new Map<string, Promise<TodayFocusResponse>>();

const requestTodayFocus = async (
	token: string,
	limit = 20,
	fetchImpl: typeof fetch = fetch
): Promise<TodayFocusResponse> => {
	const response = await fetchImpl(
		`${process.env.REACT_APP_BASE_URL_V7}dashboard/operacional/foco-hoje/buscar`,
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({ LIMITE_POR_TIPO: limit }),
		}
	);
	const body = await parseResponse(response as Response);

	if (
		response.status !== 200 ||
		body?.status !== "success" ||
		body?.code !== 0 ||
		!isQueues(body?.focoHoje)
	) {
		throw new TodayFocusApiError(
			response.status,
			typeof body?.message === "string"
				? body.message
				: "Não foi possível carregar o Foco de Hoje."
		);
	}

	return {
		limitePorTipo: Number(body.limitePorTipo) || limit,
		focoHoje: body.focoHoje,
		geradoEm: body.geradoEm || null,
	};
};

export const fetchTodayFocus = (
	token: string,
	limit = 20,
	fetchImpl: typeof fetch = fetch
): Promise<TodayFocusResponse> => {
	if (fetchImpl !== fetch) return requestTodayFocus(token, limit, fetchImpl);
	const key = `${token}:${limit}`;
	const current = todayFocusRequests.get(key);
	if (current) return current;
	const request = requestTodayFocus(token, limit, fetchImpl).finally(() =>
		todayFocusRequests.delete(key)
	);
	todayFocusRequests.set(key, request);
	return request;
};

export const registerTodayFocusEvent = async (
	token: string,
	item: TodayFocusItem,
	event: TodayFocusEvent,
	idempotencyKey: string,
	fetchImpl: typeof fetch = fetch
) => {
	const response = await fetchImpl(
		`${process.env.REACT_APP_BASE_URL_V7}dashboard/operacional/foco-hoje/eventos`,
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
				"Idempotency-Key": idempotencyKey,
			},
			body: JSON.stringify({
				ID_USUARIO: item.ID_USUARIO,
				ID_EXECUCAO_REFERENCIA: item.ID_EXECUCAO_REFERENCIA,
				TIPO: item.FOCO_TIPO,
				EVENTO: event,
			}),
		}
	);
	const body = await parseResponse(response as Response);

	if (
		response.status !== 200 ||
		body?.status !== "success" ||
		body?.code !== 0 ||
		!body?.evento
	) {
		throw new TodayFocusApiError(
			response.status,
			typeof body?.message === "string"
				? body.message
				: "Não foi possível registrar a ação."
		);
	}

	return body.evento;
};

export const todayFocusItemIdentity = (item: TodayFocusItem) =>
	`${item.FOCO_TIPO}:${item.ID_USUARIO}:${item.ID_EXECUCAO_REFERENCIA}`;
